import { useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import LaunchIcon from '@mui/icons-material/Launch'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import StyledTable from '@components/StyledTable'
import { formatDate, formatDateTime } from '@util/date-util'

import { StyledIconLink } from '../../theme/components/StyledIconLink'
import { useValuelists } from '../../valuelists/hooks/useValuelists'
import { useGebiedswisselsSearch } from '../hooks/useGebiedswisselsSearch'

import { GebiedswisselsFilters } from './GebiedswisselsFilters'
import { CreateGebiedswissel } from './dialogs/CreateGebiedswissel'

const headerCells = [
  { id: 'projectid', numeric: false, label: 'Project id' },
  { id: 'sourceGebied', numeric: false, label: 'Bron gebied' },
  { id: 'gebied', numeric: false, label: 'Doel gebied' },
  { id: 'description', numeric: false, label: 'Omschrijving' },
  { id: 'status', numeric: false, label: 'Status', fixed: 200 },
  { id: 'created.date', numeric: false, label: 'Aangemaakt', fixed: 200 },
  { id: 'changed', numeric: false, label: 'Bijgewerkt', fixed: 200 },
  {
    id: 'planned',
    numeric: false,
    label: 'Gepland',
    fixed: 200,
  },
  { id: 'filler' },
] as const

export const WISSEL_STATUSES = {
  open: 'Open',
  failed: 'Gefaald',
  done: 'Successvol afgerond',
}

export const Gebiedswissels = () => {
  const { data: valuelists } = useValuelists()
  const [open, setOpen] = useState(false)

  const {
    data: { items: gebiedswissels, count, skip, limit },
    isLoading,
    isError,
    initialFilters,
    onChangeFilters,
    onChangePage,
    filters,
    onReset,
    onRefresh,
  } = useGebiedswisselsSearch()

  return (
    <>
      <GebiedswisselsFilters
        onChange={onChangeFilters}
        filters={filters}
        initialFilters={initialFilters}
        filterOptions={valuelists}
        onCreate={() => setOpen(true)}
        onRefresh={onRefresh}
        onReset={onReset}
      />
      <StyledTable
        headerCells={headerCells}
        variant="details"
        loading={isLoading}
        stats={{ count, skip, limit }}
        onChangePage={onChangePage}
        isError={isError}
        {...({} as any)}
      >
        {gebiedswissels.map((gebiedswissel) => {
          const {
            _id,
            projectId,
            omschrijving,
            created,
            changed,
            sourceGebiedscodeLabel,
            gebiedscodeLabel,
            geplandeUitvoeringsdatum,
          } = gebiedswissel

          return (
            <TableRow key={_id}>
              <TableCell>{projectId}</TableCell>
              <TableCell>{sourceGebiedscodeLabel}</TableCell>
              <TableCell>{gebiedscodeLabel}</TableCell>
              <TableCell>{omschrijving}</TableCell>
              <TableCell>
                <GebiedswisselStatus gebiedswissel={gebiedswissel} />
              </TableCell>
              <TableCell>{formatDateTime(created.date)}</TableCell>
              <TableCell>{formatDateTime(changed)}</TableCell>
              <TableCell>{formatDate(geplandeUitvoeringsdatum)}</TableCell>
              <TableCell>
                <StyledIconLink
                  title={`Navigeer naar project ${projectId}`}
                  icon={<LaunchIcon />}
                  href={`/aansluiting/project/${projectId}`}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </StyledTable>
      {open && (
        <CreateGebiedswissel
          onClose={() => setOpen(false)}
          gebieden={valuelists.gebieden}
        />
      )}
    </>
  )
}

function GebiedswisselStatus({ gebiedswissel }) {
  const { status, error } = gebiedswissel
  const chipColor =
    status === 'done'
      ? 'green'
      : status === 'open'
        ? 'rgb(61, 108, 180)'
        : 'red'

  const iconStyle = {
    color: chipColor,
    backgroundColor: 'white',
    cursor: 'default',
  }
  const chipIcon =
    status === 'done' ? (
      <DoneIcon sx={iconStyle} /> // if done
    ) : status === 'open' ? (
      <FormatListNumberedIcon sx={iconStyle} /> // if open
    ) : (
      <CloseIcon sx={iconStyle} />
    ) // if failed

  return (
    <Grid container spacing={1}>
      <Tooltip title={error || ''}>
        <Chip
          label={WISSEL_STATUSES[status]}
          variant="outlined"
          sx={{
            backgroundColor: 'white',
            borderColor: chipColor,
            color: chipColor,
          }}
          avatar={<Avatar>{chipIcon}</Avatar>}
        />
      </Tooltip>
    </Grid>
  )
}
